// Handing the finished page over.
//
// THE CHASSIS HELPERS DROP THE LINK, and the link is the entire product.
// `core/native.js` calls Share.share({ title, files }) and `core/export.js`
// calls navigator.share({ files, title }) — neither passes `text` or `url`, so
// the reply address silently vanishes and the loop dies. src/core/** is
// factory-owned and replaced on upgrade, so this is the app's own module built
// on the primitives those files export.
//
// The URL goes in `text`, never `url`: several Safari versions drop `url`
// outright when `files` are present, and both iMessage and WhatsApp linkify a
// bare address in the text body anyway.

import { plugin, isNative } from '../core/native.js';
import { shareOrDownload } from '../core/export.js';

/** Canvas -> PNG blob. Not JPEG: ringing turns a 1-bit dither into grey mush.
 *  Not WebP either — WhatsApp treats a .webp as a STICKER. */
export function toPng(canvas) {
  if (canvas.convertToBlob) return canvas.convertToBlob({ type: 'image/png' });
  return new Promise((res) => canvas.toBlob(res, 'image/png'));
}

const toBase64 = (blob) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onerror = rej;
  fr.onload = () => res(String(fr.result).split(',')[1]);
  fr.readAsDataURL(blob);
});

/**
 * @returns {Promise<'shared'|'aborted'|'downloaded'|null>}
 *
 * 'aborted' matters: a dismissed share sheet is NOT a send, and the caller has
 * to say so. Someone with impaired working memory watched the transmission,
 * closed the sheet by reflex, and believed a fax had reached a named person.
 */
/** `.gif` for an animated page, `.png` for a still. Anything else is a bug upstream. */
const extFor = (blob) => (blob?.type === 'image/gif' ? 'gif' : 'png');

export async function shareFax(blob, { text = '', title = 'FAX', filesOnly = false } = {}) {
  // filesOnly: no caption at all. Slack, and apps like it, take the URL out
  // of a caption and post a link preview instead of the picture.
  if (filesOnly) text = '';
  const ext = extFor(blob);
  const mime = ext === 'gif' ? 'image/gif' : 'image/png';
  if (isNative) {
    try {
      const Filesystem = plugin('Filesystem');
      const Share = plugin('Share');
      if (Filesystem && Share) {
        const name = `fax-${Date.now()}.${ext}`;
        const w = await Filesystem.writeFile({
          path: name, data: await toBase64(blob), directory: 'CACHE',
        });
        await Share.share(text ? { title, text, files: [w.uri] } : { title, files: [w.uri] });
        return 'shared';
      }
    } catch (e) {
      if (isCancel(e)) return 'aborted';
      // fall through to the web path rather than losing the fax
    }
  }

  const file = new File([blob], `fax.${ext}`, { type: mime });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share(text ? { files: [file], text, title } : { files: [file], title });
      return 'shared';
    } catch (e) {
      if (e?.name === 'AbortError') return 'aborted';
    }
  }

  // Desktop, and anything without a share sheet. A file landing in Downloads is
  // a poor finale, so the caller offers COPY IMAGE beside it.
  const res = await shareOrDownload(blob, `fax.${ext}`, title);
  return res === 'shared' ? 'shared' : res === 'aborted' ? 'aborted' : 'downloaded';
}

/** Desktop needs this, because "it downloaded" is not an ending. */
export async function copyImage(blob) {
  try {
    if (!navigator.clipboard?.write) return false;
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    return true;
  } catch { return false; }
}

function isCancel(e) {
  const m = String(e?.message || e || '').toLowerCase();
  return m.includes('cancel') || m.includes('abort') || m.includes('dismiss');
}
